"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import { applyExclusivePosTax } from "@/modules/merchant/domain/pos-exclusive-tax";
import type {
  CreatePosReturnBody,
  PosLotItem,
  PosReturnRefundMethod,
  PosSaleDetailResponse,
  PosSerialItem,
  ShopSellableCatalogRow,
} from "@/modules/merchant/presentation/api/client";
import {
  useCreatePosReturnMutation,
  usePosCheckoutMutation,
  usePosSellableBrowseQuery,
} from "@/modules/merchant/presentation/api/queries";
import { ShopPosLotPickModal } from "@/modules/merchant/presentation/ui/ShopPosLotPickModal";
import { ShopPosSerialPickModal } from "@/modules/merchant/presentation/ui/ShopPosSerialPickModal";
import { formatPrice } from "@/shared/lib/currency";

/** DB ຄ່າເກົ່າ `serial` ທຳງານຄື lot_serial */
function usesSerialFlow(m: string | undefined): boolean {
  return m === "lot_serial" || m === "serial";
}

type ReturnLineState = {
  saleItemId: string;
  productName: string;
  soldQty: number;
  /** ຈຳນວນທີ່ຄືນໄດ້ອີກ = soldQty − ຄືນໄປແລ້ວ */
  returnableQty: number;
  lineTotal: number;
  qty: number;
};

type NewLineState = {
  /** id ຕໍ່ບັນທັດ (serial/lot ຕ່າງ lot ແຍກບັນທັດ) */
  lineId: string;
  skuId: string;
  name: string;
  unitPrice: number;
  qty: number;
  trackingMode: string;
  lotId?: string;
  lotNo?: string;
  /** ຈຳນວນສູງສຸດຂອງ lot ນີ້ */
  lotQtyCap?: number;
  serialId?: string;
  serialNo?: string;
  /** ຄຳອະທິບາຍ lot/serial ສະແດງໃຕ້ຊື່ */
  detailLabel?: string;
};

const REFUND_METHODS: { value: PosReturnRefundMethod; label: string }[] = [
  { value: "cash", label: "ເງິນສົດ" },
  { value: "transfer", label: "ໂອນ" },
];

function newIdempotencyKey(): string {
  return `exch-${crypto.randomUUID()}`;
}

export function ShopPosReturnModal({
  open,
  onOpenChange,
  shopId,
  branchId,
  detail,
  mode,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  shopId: string | undefined;
  branchId: string | undefined;
  detail: PosSaleDetailResponse | null;
  mode: "return" | "exchange";
  onDone?: () => void;
}) {
  const createMut = useCreatePosReturnMutation(shopId);
  const checkoutMut = usePosCheckoutMutation(shopId, branchId);

  // ໃຊ້ mode ໂດຍກົງ (return/exchange) — ບໍ່ມີ toggle ໃນ modal
  const type = mode;
  const [lines, setLines] = useState<ReturnLineState[]>([]);
  const [newLines, setNewLines] = useState<NewLineState[]>([]);
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] =
    useState<PosReturnRefundMethod>("cash");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const PAGE = 8;
  // lot/serial picker ສຳລັບສິນຄ້າໃໝ່ທີ່ຕິດຕາມ lot/serial
  const [lotPickRow, setLotPickRow] = useState<ShopSellableCatalogRow | null>(
    null,
  );
  const [serialPickRow, setSerialPickRow] =
    useState<ShopSellableCatalogRow | null>(null);

  const currency = detail?.currency ?? "LAK";

  useEffect(() => {
    if (!detail) {
      setLines([]);
      setNewLines([]);
      return;
    }
    setLines(
      detail.lines.map((l) => ({
        saleItemId: l.id,
        productName: l.productName,
        soldQty: l.qty,
        returnableQty: Math.max(0, l.qty - (l.returnedQty ?? 0)),
        lineTotal: l.lineTotal,
        qty: 0,
      })),
    );
    setNewLines([]);
    setReason("");
    setRefundMethod("cash");
    setSearch("");
    setOffset(0);
  }, [detail]);

  // reset ໜ້າ ເມື່ອປ່ຽນຄຳຄົ້ນ
  useEffect(() => {
    setOffset(0);
  }, [search]);

  // ---- ຄืนของเก่า (both modes) ----
  const selectWholeBill = () =>
    setLines((prev) => prev.map((l) => ({ ...l, qty: l.returnableQty })));
  const clearOld = () =>
    setLines((prev) => prev.map((l) => ({ ...l, qty: 0 })));
  const setQty = (id: string, qty: number) =>
    setLines((prev) =>
      prev.map((l) =>
        l.saleItemId === id
          ? { ...l, qty: Math.max(0, Math.min(qty, l.returnableQty)) }
          : l,
      ),
    );

  // ---- สินค้าใหม่ (exchange) — แสดงรายการเสมอ + limit/offset ----
  const browseQ = usePosSellableBrowseQuery(
    type === "exchange" && open ? shopId : undefined,
    { q: search.trim() || undefined, limit: PAGE, offset },
  );
  const browseRows = browseQ.data?.data ?? [];
  const browseTotal = browseQ.data?.meta.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE < browseTotal;

  // ເລືອກສິນຄ້າໃໝ່ຈາກລາຍການ — ຖ້າ lot/serial ໃຫ້ເປີດ picker ກ່ອນ
  const pickNew = (row: ShopSellableCatalogRow) => {
    if (row.currency !== currency) {
      toast.error("ສະກຸນສິນຄ້າໃໝ່ບໍ່ກົງກັບບິນ");
      return;
    }
    if (usesSerialFlow(row.trackingMode)) {
      setSerialPickRow(row);
      return;
    }
    if (row.trackingMode === "lot") {
      setLotPickRow(row);
      return;
    }
    // none — ເພີ່ມກົງ (ລວມ qty ຖ້າມີແລ້ວ)
    setNewLines((prev) => {
      const i = prev.findIndex(
        (n) => n.skuId === row.id && n.trackingMode === "none",
      );
      if (i >= 0) {
        return prev.map((n, idx) => (idx === i ? { ...n, qty: n.qty + 1 } : n));
      }
      return [
        ...prev,
        {
          lineId: crypto.randomUUID(),
          skuId: row.id,
          name: row.productName,
          unitPrice: row.price,
          qty: 1,
          trackingMode: "none",
        },
      ];
    });
  };

  const commitLot = (row: ShopSellableCatalogRow, lot: PosLotItem) => {
    setNewLines((prev) => {
      const i = prev.findIndex((n) => n.skuId === row.id && n.lotId === lot.id);
      if (i >= 0) {
        return prev.map((n, idx) =>
          idx === i ? { ...n, qty: Math.min(n.qty + 1, lot.qty) } : n,
        );
      }
      return [
        ...prev,
        {
          lineId: crypto.randomUUID(),
          skuId: row.id,
          name: row.productName,
          unitPrice: row.price,
          qty: 1,
          trackingMode: "lot",
          lotId: lot.id,
          lotNo: lot.lotNo,
          lotQtyCap: lot.qty,
          detailLabel: `Lot ${lot.lotNo}`,
        },
      ];
    });
    setLotPickRow(null);
  };

  const commitSerial = (row: ShopSellableCatalogRow, serial: PosSerialItem) => {
    setNewLines((prev) => {
      if (prev.some((n) => n.serialId === serial.id)) {
        toast.error("serial ນີ້ຖືກເລືອກແລ້ວ");
        return prev;
      }
      return [
        ...prev,
        {
          lineId: crypto.randomUUID(),
          skuId: row.id,
          name: row.productName,
          unitPrice: row.price,
          qty: 1,
          trackingMode: row.trackingMode,
          serialId: serial.id,
          serialNo: serial.serialNo,
          detailLabel: `SN ${serial.serialNo}`,
        },
      ];
    });
    setSerialPickRow(null);
  };

  const setNewQty = (lineId: string, qty: number) =>
    setNewLines((prev) =>
      prev
        .map((n) =>
          n.lineId === lineId
            ? {
                ...n,
                qty:
                  n.lotQtyCap != null
                    ? Math.max(0, Math.min(qty, n.lotQtyCap))
                    : Math.max(0, qty),
              }
            : n,
        )
        .filter((n) => n.qty > 0),
    );

  const selectedOld = useMemo(() => lines.filter((l) => l.qty > 0), [lines]);
  const returnRefund = useMemo(
    () =>
      selectedOld.reduce((s, l) => s + (l.lineTotal * l.qty) / l.soldQty, 0),
    [selectedOld],
  );
  // VAT ຂອງບິນປ່ຽນໃໝ່ — ໃຊ້ອັດຕາຈາກບິນຕົ້ນສະບັບ (ຮ້ານດຽວກັນ) ໃຫ້ຕົງກັບ server checkout
  const exchangeTaxPercent = detail?.taxPercent ?? 0;
  const newSubtotal = useMemo(
    () => newLines.reduce((s, n) => s + n.unitPrice * n.qty, 0),
    [newLines],
  );
  // ຍອດບິນໃໝ່ລວມ VAT (exclusive) — ຕ້ອງຈ່າຍໃຫ້ຄົບ ບໍ່ດັ່ງນັ້ນ checkout ຈະ error
  const { grandTotal: newTotal } = useMemo(
    () => applyExclusivePosTax(newSubtotal, exchangeTaxPercent),
    [newSubtotal, exchangeTaxPercent],
  );
  const diff = newTotal - returnRefund; // >0 ลูกค้าจ่ายเพิ่ม, <0 ร้านคืน

  const canSubmit =
    !!shopId &&
    !!branchId &&
    !!detail &&
    selectedOld.length > 0 &&
    (type === "return" || newLines.length > 0);

  const busy = createMut.isPending || checkoutMut.isPending;

  const submit = async () => {
    if (!shopId || !branchId || !detail) return;
    if (selectedOld.length === 0) {
      toast.error("ເລືອກສິນຄ້າເກົ່າຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }
    if (type === "exchange" && newLines.length === 0) {
      toast.error("ເລືອກສິນຄ້າໃໝ່ຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }

    try {
      let exchangeSaleId: string | undefined;

      // exchange: ຂາຍບິນໃໝ່ກ່ອນ (ຈ່າຍເຕັມຍອດໃໝ່) — ສ່ວນຕ່າງເກີດຈາກ ຄືນ - ຂາຍ
      if (type === "exchange") {
        const res = await checkoutMut.mutateAsync({
          idempotencyKey: newIdempotencyKey(),
          body: {
            lines: newLines.map((n) => {
              if (n.serialId) {
                return {
                  shopProductSkuId: n.skuId,
                  qty: 1,
                  serials: [{ serialId: n.serialId }],
                };
              }
              if (n.lotId) {
                return {
                  shopProductSkuId: n.skuId,
                  qty: n.qty,
                  lotId: n.lotId,
                };
              }
              return { shopProductSkuId: n.skuId, qty: n.qty };
            }),
            payments: [
              { method: "cash", amount: newTotal, reference: undefined },
            ],
            invoiceCurrency: currency as "LAK" | "THB" | "USD",
            // ສົ່ງ VAT ໃຫ້ຕົງກັບຍອດທີ່ຈ່າຍ (newTotal = ຍອດລວມ VAT)
            taxPercent: exchangeTaxPercent,
          },
        });
        exchangeSaleId = res.saleId;
      }

      const body: CreatePosReturnBody = {
        originalSaleId: detail.id,
        type,
        reason: reason.trim() || undefined,
        refundMethod,
        exchangeSaleId,
        // ຄืນ = ເຂົ້າສະຕ໋ອກທັງໝົດ (restock ສະເໝີ)
        lines: selectedOld.map((l) => ({
          saleItemId: l.saleItemId,
          qty: l.qty,
          restock: true,
        })),
      };
      const ret = await createMut.mutateAsync({ branchId, body });
      toast.success(
        type === "exchange"
          ? `ປ່ຽນສິນຄ້າສຳເລັດ — ${ret.returnNumber}`
          : `ຄືນສິນຄ້າສຳເລັດ — ${ret.returnNumber} (ຄືນ ${formatPrice(ret.refundTotal, currency)})`,
      );
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດດຳເນີນການໄດ້");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton
          className="flex max-h-[min(92dvh,calc(100dvh-1rem))] w-[calc(100vw-1.5rem)] max-w-[min(72rem,calc(100vw-1.5rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(72rem,calc(100vw-1.5rem))]"
        >
          <DialogHeader className="shrink-0 space-y-1 border-border border-b bg-muted/20 px-5 py-4 text-start">
            <DialogTitle className="font-heading text-lg">
              {type === "exchange" ? "ປ່ຽນສິນຄ້າ" : "ຄືນສິນຄ້າ"}
              {detail ? ` — ບິນ #${detail.saleNumber}` : ""}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {type === "exchange"
                ? "ເລືອກສິນຄ້າເກົ່າທີ່ຄືນ ແລະ ສິນຄ້າໃໝ່ທີ່ແລກ"
                : "ເລືອກສິນຄ້າ ແລະ ຈຳນວນທີ່ຕ້ອງການຄືນ (ເຂົ້າສະຕ໋ອກ)"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {/* ---- ຄืนของเก่า ---- */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">ສິນຄ້າເກົ່າທີ່ຄືນ (ເຂົ້າສະຕ໋ອກ)</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={selectWholeBill}
                  >
                    ຄືນທັງບິນ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={clearOld}
                  >
                    ລ້າງ
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-100 text-left text-sm">
                  <thead className="border-border border-b bg-muted/40">
                    <tr>
                      <th className="px-3 py-2">ສິນຄ້າ</th>
                      <th className="px-3 py-2 text-center">ຂາຍ</th>
                      <th className="px-3 py-2 text-center">ຈຳນວນຄືນ</th>
                      <th className="px-3 py-2 text-right">ເງິນຄືນ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const lineRefund =
                        l.qty > 0 ? (l.lineTotal * l.qty) / l.soldQty : 0;
                      const alreadyReturned = l.soldQty - l.returnableQty;
                      const fullyReturned = l.returnableQty <= 0;
                      return (
                        <tr
                          key={l.saleItemId}
                          className="border-border border-b"
                        >
                          <td className="px-3 py-2">
                            {l.productName}
                            {alreadyReturned > 0 ? (
                              <span className="ml-1 text-muted-foreground text-xs">
                                (ຄືນແລ້ວ {alreadyReturned}/{l.soldQty})
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums">
                            {l.soldQty}
                          </td>
                          <td className="px-3 py-2">
                            {fullyReturned ? (
                              <p className="text-center text-muted-foreground text-xs">
                                ຄືນຄົບແລ້ວ
                              </p>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="size-7 p-0"
                                  disabled={l.qty <= 0}
                                  onClick={() =>
                                    setQty(l.saleItemId, l.qty - 1)
                                  }
                                >
                                  −
                                </Button>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  className="h-8 w-14 text-center"
                                  value={String(l.qty)}
                                  onChange={(e) =>
                                    setQty(
                                      l.saleItemId,
                                      Number(
                                        e.target.value.replace(/[^0-9]/g, ""),
                                      ) || 0,
                                    )
                                  }
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="size-7 p-0"
                                  disabled={l.qty >= l.returnableQty}
                                  onClick={() =>
                                    setQty(l.saleItemId, l.qty + 1)
                                  }
                                >
                                  +
                                </Button>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatPrice(lineRefund, currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ---- สินค้าใหม่ (exchange) ---- */}
            {type === "exchange" ? (
              <div className="space-y-2">
                <Label className="text-xs">ສິນຄ້າໃໝ່ (ແລກປ່ຽນ)</Label>
                <Input
                  className="h-9"
                  placeholder="ຄົ້ນຊື່ / SKU ສິນຄ້າໃໝ່…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="space-y-1 rounded-lg border border-border p-1">
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {browseQ.isLoading ? (
                      <p className="p-2 text-muted-foreground text-xs">
                        ກຳລັງໂຫຼດ…
                      </p>
                    ) : browseRows.length === 0 ? (
                      <p className="p-2 text-muted-foreground text-xs">
                        ບໍ່ພົບສິນຄ້າ
                      </p>
                    ) : (
                      browseRows.map((r) => (
                        <button
                          type="button"
                          key={r.id}
                          className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60"
                          onClick={() => pickNew(r)}
                        >
                          <span className="min-w-0 truncate">
                            {r.productName}{" "}
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {r.shopSku}
                            </span>
                            {r.trackingMode === "lot" ? (
                              <span className="ml-1 text-[10px] text-amber-600">
                                (lot)
                              </span>
                            ) : usesSerialFlow(r.trackingMode) ? (
                              <span className="ml-1 text-[10px] text-sky-600">
                                (serial)
                              </span>
                            ) : null}
                          </span>
                          <span className="shrink-0 tabular-nums">
                            {formatPrice(r.price, r.currency)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                  {browseTotal > PAGE ? (
                    <div className="flex items-center justify-between border-border border-t px-2 pt-1.5 text-muted-foreground text-xs">
                      <span>
                        {offset + 1}–{Math.min(offset + PAGE, browseTotal)} /{" "}
                        {browseTotal}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7"
                          disabled={!hasPrev}
                          onClick={() => setOffset(Math.max(0, offset - PAGE))}
                        >
                          ກ່ອນ
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7"
                          disabled={!hasNext}
                          onClick={() => setOffset(offset + PAGE)}
                        >
                          ຕໍ່ໄປ
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {newLines.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-100 text-left text-sm">
                      <thead className="border-border border-b bg-muted/40">
                        <tr>
                          <th className="px-3 py-2">ສິນຄ້າໃໝ່</th>
                          <th className="px-3 py-2 text-center">ຈຳນວນ</th>
                          <th className="px-3 py-2 text-right">ລວມ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newLines.map((n) => {
                          const isSerial = !!n.serialId;
                          return (
                            <tr
                              key={n.lineId}
                              className="border-border border-b"
                            >
                              <td className="px-3 py-2">
                                {n.name}
                                {n.detailLabel ? (
                                  <span className="ml-1 text-muted-foreground text-xs">
                                    · {n.detailLabel}
                                  </span>
                                ) : null}
                              </td>
                              <td className="px-3 py-2">
                                {isSerial ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <span className="tabular-nums">1</span>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-destructive"
                                      onClick={() => setNewQty(n.lineId, 0)}
                                    >
                                      ລົບ
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="size-7 p-0"
                                      onClick={() =>
                                        setNewQty(n.lineId, n.qty - 1)
                                      }
                                    >
                                      −
                                    </Button>
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      className="h-8 w-14 text-center"
                                      value={String(n.qty)}
                                      onChange={(e) =>
                                        setNewQty(
                                          n.lineId,
                                          Number(
                                            e.target.value.replace(
                                              /[^0-9]/g,
                                              "",
                                            ),
                                          ) || 0,
                                        )
                                      }
                                    />
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="size-7 p-0"
                                      disabled={
                                        n.lotQtyCap != null &&
                                        n.qty >= n.lotQtyCap
                                      }
                                      onClick={() =>
                                        setNewQty(n.lineId, n.qty + 1)
                                      }
                                    >
                                      +
                                    </Button>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {formatPrice(n.unitPrice * n.qty, currency)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* ---- วิธีคืนเงิน + เหตุผล ---- */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">ວິທີຄືນເງິນ</Label>
                <div className="flex flex-wrap gap-1.5">
                  {REFUND_METHODS.map((m) => (
                    <Button
                      key={m.value}
                      type="button"
                      size="sm"
                      variant={refundMethod === m.value ? "default" : "outline"}
                      onClick={() => setRefundMethod(m.value)}
                    >
                      {m.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs" htmlFor="return-reason">
                  ເຫດຜົນ (ທາງເລືອກ)
                </Label>
                <Input
                  id="return-reason"
                  className="h-9"
                  placeholder="ເຊັ່ນ ສິນຄ້າຊຳລຸດ, ລູກຄ້າປ່ຽນໃຈ"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 flex-col items-stretch gap-2 border-border border-t bg-muted/20 px-5 py-4">
            {type === "exchange" ? (
              <div className="flex flex-col gap-0.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ເງິນຄືນ (ເກົ່າ)</span>
                  <span className="tabular-nums">
                    {formatPrice(returnRefund, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ຍອດໃໝ່</span>
                  <span className="tabular-nums">
                    {formatPrice(newTotal, currency)}
                  </span>
                </div>
                <div className="flex justify-between border-border border-t pt-1 font-semibold">
                  <span>
                    {diff > 0 ? "ລູກຄ້າຈ່າຍເພີ່ມ" : diff < 0 ? "ຮ້ານຄືນເງິນ" : "ສ່ວນຕ່າງ"}
                  </span>
                  <span className="tabular-nums">
                    {formatPrice(Math.abs(diff), currency)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ລວມເງິນຄືນ</span>
                <span className="font-bold tabular-nums">
                  {formatPrice(returnRefund, currency)}
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                disabled={!canSubmit || busy}
                onClick={submit}
              >
                {type === "exchange" ? "ຢືນຢັນປ່ຽນ" : "ຢືນຢັນຄືນ"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {shopId && branchId ? (
        <>
          <ShopPosLotPickModal
            open={!!lotPickRow}
            onOpenChange={(o) => {
              if (!o) setLotPickRow(null);
            }}
            shopId={shopId}
            branchId={branchId}
            row={lotPickRow}
            onPick={commitLot}
          />
          <ShopPosSerialPickModal
            open={!!serialPickRow}
            onOpenChange={(o) => {
              if (!o) setSerialPickRow(null);
            }}
            shopId={shopId}
            branchId={branchId}
            row={serialPickRow}
            onPick={commitSerial}
          />
        </>
      ) : null}
    </>
  );
}
